// Helpers for the camera and microphone check that runs before the Video KYC recording.

export const MIC_SPEECH_LEVEL = 0.03; // loudness (0..1) above which we count "someone is speaking"
export const MIC_SPEECH_TICKS = 4; // loud samples (150 ms apart) that prove the microphone works
export const CAMERA_MIN_BRIGHTNESS = 12; // average brightness (0..255) below which the picture counts as black or covered
export const CAMERA_GOOD_FRAMES = 3; // consecutive bright frames that prove the camera works
export const CHECK_INTERVAL_MS = 150;
export const MIC_HINT_AFTER_MS = 8000;
export const CAMERA_HINT_AFTER_MS = 5000;
export const LOCATION_ATTEMPT_TIMEOUT_MS = 10000; // one try; a second, less exact try follows when the first one times out

/** Loudness (0..1, RMS) of 8-bit time-domain samples from an AnalyserNode. Silence is all 128. */
export const micLevel = (samples) => {
  if (!samples || samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const d = (samples[i] - 128) / 128;
    sum += d * d;
  }
  return Math.sqrt(sum / samples.length);
};

/** Average brightness (0..255) of RGBA pixel data, e.g. from canvas getImageData(). */
export const averageBrightness = (rgba) => {
  if (!rgba || rgba.length < 4) return 0;
  let total = 0;
  const pixels = Math.floor(rgba.length / 4);
  for (let i = 0; i < pixels; i += 1) {
    total += 0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2];
  }
  return total / pixels;
};

const askForPosition = (options) =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = (position && position.coords) || {};
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          reject({ code: 2 });
          return;
        }
        resolve({ latitude, longitude, accuracy: Number.isFinite(accuracy) ? accuracy : null });
      },
      reject,
      options
    );
  });

/**
 * The customer's position right now: { latitude, longitude, accuracy (metres) }.
 * First a high accuracy try (GPS on a phone); when that cannot be determined or times out, one more try with normal
 * accuracy. A refusal is never retried. Rejects with { code }: 1 refused, 2 unavailable, 3 timed out, 0 not supported.
 */
export const getLocation = async () => {
  if (typeof navigator === "undefined" || !navigator.geolocation || !navigator.geolocation.getCurrentPosition) {
    throw Object.assign(new Error("Geolocation is not supported"), { code: 0 });
  }
  try {
    return await askForPosition({ enableHighAccuracy: true, timeout: LOCATION_ATTEMPT_TIMEOUT_MS, maximumAge: 0 });
  } catch (error) {
    if (error && error.code === 1) throw error;
    return askForPosition({ enableHighAccuracy: false, timeout: LOCATION_ATTEMPT_TIMEOUT_MS, maximumAge: 0 });
  }
};

/** What to tell the customer when the location cannot be read. */
export const locationErrorMessage = (error) => {
  switch (error && error.code) {
    case 1:
      return "Location access is blocked. Tap the lock icon in the address bar, allow Location, then press Test again.";
    case 3:
      return "Finding your location took too long. Check that Location is turned on and move near a window or outside, then press Test again.";
    case 0:
      return "Your browser cannot share your location. Please open this link in Chrome or Safari.";
    default:
      return "We could not find your location. Turn on Location on your device, then press Test again.";
  }
};

/** What to tell the customer when getUserMedia fails. */
export const mediaErrorMessage = (error) => {
  switch (error && error.name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return "Camera or microphone access is blocked. Tap the lock or camera icon in the address bar, allow camera and microphone, then press Test again.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "We could not find a camera or microphone on this device. Connect one and press Test again.";
    case "NotReadableError":
    case "TrackStartError":
      return "Your camera or microphone is being used by another app. Close that app and press Test again.";
    default:
      return "We could not start your camera and microphone. Please check them and press Test again.";
  }
};
